import { fetchJSON as fetchAPI } from './client';

export type RiskLevel = 'low' | 'moderate' | 'high' | 'critical';
export type RecommendationCategory = string;
export type ScenarioCase = 'best_case' | 'expected_case' | 'worst_case';
export type FeedbackAction = 'accepted' | 'rejected' | 'override' | 'partial';

export type AIForecastRecord = {
  id: string;
  tenant_id: string;
  name: string;
  statistical_forecast_id?: string;
  demand_plan_id?: string;
  node_label?: string;
  status: string;
  statistical_units: number;
  statistical_confidence: number;
  statistical_algorithm?: string;
  ai_forecast_units: number;
  confidence_score: number;
  confidence_low: number;
  confidence_high: number;
  risk_level: RiskLevel;
  opportunity_score: number;
  expected_revenue: number;
  expected_margin: number;
  inventory_impact: number;
  procurement_impact: number;
  warehouse_impact: number;
  cash_flow_impact: number;
  reasoning_summary: string;
  prompt_registry_ref?: string;
  model_registry_ref?: string;
  auto_execute: boolean;
  created_at: string;
  updated_at: string;
};

export type AgentInsight = {
  agent_type: string;
  agent_label: string;
  responsibility: string;
  summary: string;
  confidence: number;
  evidence: string[];
  adjustment_pct: number;
};

export type Explanation = {
  id: string;
  ai_forecast_id: string;
  reasoning_summary: string;
  supporting_evidence: string[];
  statistical_comparison: string;
  business_assumptions: string[];
  confidence: number;
  alternative_scenarios: string[];
  known_risks: string[];
  expected_business_outcome: string;
  related_knowledge_objects: string[];
  agent_insights: AgentInsight[];
};

export type Recommendation = {
  id: string;
  ai_forecast_id: string;
  category: RecommendationCategory;
  title: string;
  description: string;
  reasoning_summary: string;
  supporting_evidence: string[];
  confidence: number;
  expected_impact: string;
  statistical_baseline: string;
  known_risks: string[];
  source_agent: string;
  priority: number;
  auto_execute: boolean;
  status: string;
  created_at: string;
};

export type ForecastComparison = {
  ai_forecast_id: string;
  statistical_units: number;
  ai_forecast_units: number;
  deviation_pct: number;
  improvement_score: number;
  expected_accuracy: number;
  business_impact: string;
  confidence: number;
  statistical_algorithm?: string;
  comparison_summary: string;
};

export type ScenarioResult = {
  id: string;
  ai_forecast_id: string;
  name: string;
  scenario_type: string;
  case: ScenarioCase;
  forecast_units: number;
  revenue_impact: number;
  margin_impact: number;
  confidence: number;
  description: string;
  assumptions: string[];
};

export type AIForecastDashboard = {
  tenant_id: string;
  active_forecasts: number;
  total_recommendations: number;
  pending_recommendations: number;
  avg_confidence: number;
  avg_opportunity_score: number;
  high_risk_count: number;
  planner_acceptance_rate: number;
  forecast_accuracy_improvement: number;
  learning_events: number;
  agent_count: number;
};

export type AgentDefinition = {
  type: string;
  label: string;
  responsibility: string;
  inputs: string[];
  outputs: string[];
  escalation_rule: string;
};

export type GenerateAIForecastRequest = {
  name: string;
  statistical_forecast_id?: string;
  statistical_units: number;
  statistical_confidence: number;
  statistical_algorithm?: string;
  demand_plan_id?: string;
  node_label?: string;
  unit_price?: number;
  margin_pct?: number;
  promotion_active?: boolean;
  inventory_days?: number;
  ad_spend_trend?: number;
  marketplace_signal?: number;
};

export type FeedbackRequest = {
  ai_forecast_id: string;
  recommendation_id?: string;
  action: FeedbackAction;
  override_reason?: string;
  planner_notes?: string;
  accuracy_score?: number;
};

const API_BASE = import.meta.env.VITE_AIFORECAST_API ?? '/api/v1';

function aiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const aiForecastApi = {
  dashboard: () => aiFetch<AIForecastDashboard>('/ai-forecast/dashboard'),
  listForecasts: () => aiFetch<AIForecastRecord[]>('/ai-forecast'),
  getForecast: (id: string) => aiFetch<AIForecastRecord>(`/ai-forecast/${id}`),
  generateForecast: (req: GenerateAIForecastRequest) =>
    aiFetch<AIForecastRecord>('/ai-forecast/generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    }),
  listRecommendations: (forecastId?: string) =>
    aiFetch<Recommendation[]>(`/ai-forecast/recommendations${forecastId ? `?ai_forecast_id=${forecastId}` : ''}`),
  listExplanations: (forecastId?: string) =>
    aiFetch<Explanation[]>(`/ai-forecast/explanations${forecastId ? `?ai_forecast_id=${forecastId}` : ''}`),
  listScenarios: (forecastId?: string) =>
    aiFetch<ScenarioResult[]>(`/ai-forecast/scenarios${forecastId ? `?ai_forecast_id=${forecastId}` : ''}`),
  getComparison: (forecastId: string) =>
    aiFetch<ForecastComparison>(`/ai-forecast/comparison?ai_forecast_id=${forecastId}`),
  submitFeedback: (req: FeedbackRequest) =>
    aiFetch<{ id: string }>('/ai-forecast/feedback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    }),
  listAgents: () => aiFetch<AgentDefinition[]>('/ai-forecast/agents'),
};
